import React, { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModuleNav, ModuleNavItem } from '../../contexts/ModuleNavContext';
import { useMenu } from '../../auth/useMenu';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useAuth } from '../../contexts/AuthContext';

const ALL_SPRAY_NAV_ITEMS: ModuleNavItem[] = [
  { to: '/protection', labelKey: 'spray.tabs.overview', end: true, group: 'nav.groups.overview' },
  { to: '/protection/sessions', labelKey: 'spray.tabs.sessions', subject: 'spraying:sessions', group: 'nav.groups.operations' },
  { to: '/protection/my-sessions', labelKey: 'spray.tabs.mySessions', subject: 'linkedOperator', group: 'nav.groups.operations' },
  { to: '/protection/back-capture', labelKey: 'spray.tabs.backCapture', subject: 'spraying:backCapture', group: 'nav.groups.operations' },
  { to: '/protection/programs', labelKey: 'spray.tabs.programs', subject: 'spraying:programs', group: 'nav.groups.library' },
  { to: '/protection/products', labelKey: 'spray.tabs.products', subject: 'spraying:products', group: 'nav.groups.library' },
  { to: '/protection/stock-levels',    labelKey: 'spray.tabs.stockLevels',    subject: 'spraying:inventory', group: 'nav.groups.library' },
  { to: '/protection/stock-movements', labelKey: 'spray.tabs.stockMovements', subject: 'spraying:inventory', group: 'nav.groups.library' },
  { to: '/protection/machines', labelKey: 'spray.tabs.machines', subject: 'spraying:machines', group: 'nav.groups.resources' },
  { to: '/protection/operators', labelKey: 'spray.tabs.operators', subject: 'spraying:operators', group: 'nav.groups.resources' },
  { to: '/protection/weather-history', labelKey: 'spray.tabs.weatherHistory', subject: 'spraying:weatherHistory', group: 'nav.groups.tools' },
  { to: '/protection/import', labelKey: 'spray.tabs.import', subject: 'spraying:csvImport', group: 'nav.groups.tools' },
];

const SprayLayout: React.FC = () => {
  const { t } = useTranslation();
  const { setModule } = useModuleNav();
  const { user } = useAuth();
  const hasSessions = useMenu('spraying:sessions');
  const hasBackCapture = useMenu('spraying:backCapture');
  const hasPrograms = useMenu('spraying:programs');
  const hasProducts = useMenu('spraying:products');
  const hasInventory = useMenu('spraying:inventory');
  const hasMachines = useMenu('spraying:machines');
  const hasOperators = useMenu('spraying:operators');
  const hasWeatherHistory = useMenu('spraying:weatherHistory');
  const hasCsvImport = useMenu('spraying:csvImport');

  // "My Sessions" is only visible to users with an actual operator linkage (or superadmin).
  // FarmAdmin without linkage does NOT see "My Sessions" — admin is not pretending to be an operator.
  const shouldShowMySessions = !!user?.hasLinkedOperator || !!user?.superadmin;

  const filteredItems = useMemo(() => {
    const menuMap: Record<string, boolean> = {
      'spraying:sessions': hasSessions,
      'spraying:backCapture': hasBackCapture,
      'spraying:programs': hasPrograms,
      'spraying:products': hasProducts,
      'spraying:inventory': hasInventory,
      'spraying:machines': hasMachines,
      'spraying:operators': hasOperators,
      'spraying:weatherHistory': hasWeatherHistory,
      'spraying:csvImport': hasCsvImport,
    };
    return ALL_SPRAY_NAV_ITEMS.filter(item => {
      if (!item.subject) return true;
      if (item.subject === 'linkedOperator') return shouldShowMySessions;
      return menuMap[item.subject] === true;
    });
  }, [hasSessions, hasBackCapture, hasPrograms, hasProducts, hasInventory, hasMachines, hasOperators, hasWeatherHistory, hasCsvImport, shouldShowMySessions]);

  useEffect(() => {
    setModule(t('spray.title'), filteredItems);
    return () => setModule(null, null);
  }, [setModule, t, filteredItems]);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
  ]);

  return (
    <div data-testid="spray-layout">
      <Outlet />
    </div>
  );
};

export default SprayLayout;
